import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  useTheme,
  TableBody,
  TableSortLabel,
  IconButton,
  CircularProgress,
  TextField,
  Link,
  Grid,
  Collapse,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import AddReportGroup from "./AddReportGroup";
import ConfirmDialog from "../ConfirmDialog";
import axios from "../../utils/axiosInstance";
import LastMonthReportDialog from "./lastMonthReportDialog";
import LastSixMonthReportDialog from "./lastSixMonthReportDialog";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ReportGroupDetails from "./reportGroupDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
const ReportGroups = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const [loadingReportGroups, setLoadingReportGroups] = useState(false);
  const [reportGroups, setReportGroups] = useState([]);
  const [errorFetching, setErrorFetching] = useState(null);
  //show add report group form
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Name");
  // For report popup
  const [lastMonthReportDialogOpen, setLastMonthReportDialogOpen] =
    useState(false);
  const [lastSixMonthReportDialogOpen, setLastSixMonthReportDialogOpen] =
    useState(false);
  const [lastYearReportDialogOpen, setLastYearReportDialogOpen] =
    useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // grroup details page
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetailsGroup, setSelectedDetailsGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const addReportGroup = () => setShowAddForm(true);

  const goToUnitsSubs = (grp) => {
    setSelectedDetailsGroup(grp);
    setShowDetails(true);
  };

  const handleDeleteClick = (grp) => {
    setGroupToDelete(grp);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;
    try {
      setConfirmOpen(false);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/groups/${groupToDelete.id}`,
        {
          data: { UI_Description: `${getUIDescription()}\n Delete report Group`, },
        }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Report Group Successfully Deleted");
        await fetchReportGroups();
        setGroupToDelete(null);
      } else {
        toast.error("Failed to delete report group");
      }
    } catch (err) {
      console.error("❌ Error deleting report group:", err);
      toast.error("Failed to delete report group");
    }
  };

  const fetchReportGroups = async () => {
    try {
      setLoadingReportGroups(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/reportGroups`
      );
      setReportGroups(response.data || []);
    } catch (err) {
      console.error("Failed to fetch report groups:", err);
      setErrorFetching("Error fetching report groups");
    } finally {
      setLoadingReportGroups(false);
    }
  };

  useEffect(() => {
    fetchReportGroups();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const sorted = [...reportGroups];
    sorted.sort((a, b) => {
      let aValue = a[orderBy.toLowerCase().replace(/\s+/g, "_")];
      let bValue = b[orderBy.toLowerCase().replace(/\s+/g, "_")];

      // fallback for known field names
      if (orderBy === "Name") {
        aValue = a.name || "";
        bValue = b.name || "";
      } else if (orderBy === "Date Created") {
        aValue = new Date(a.date_created);
        bValue = new Date(b.date_created);
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [reportGroups, order, orderBy]);

  const handleOpenLastReportDialog = async (grp) => {
    setSelectedGroup(grp);
    setLastMonthReportDialogOpen(true);
  };

  const handleOpenLastSixReportDialog = async (grp) => {
    setSelectedGroup(grp);
    setLastSixMonthReportDialogOpen(true);
  };

  const handleOpenLastYearReportDialog = async (grp) => {
    setSelectedGroup(grp);
    setLastYearReportDialogOpen(true);
  };

  const handleSendEmail = async (grp) => {
    if (!grp) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/sendReports/monthlyReport/reportGroup/${grp.id}`,
        {
          year: new Date().getFullYear(),
          month: new Date().getMonth(),
          UI_Description: `${getUIDescription()}\n Send Email Monthly report`,
        }
      );
      if (response.data.success === true) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("❌ Error sending email:", err);
      toast.error("Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = sortedData.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      new Date(row.date_created)
        .toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const columns = [
    { label: "Name", hideOnMobile: false },
    { label: "Customer Group", hideOnMobile: false },
    { label: "Description", hideOnMobile: false },
    { label: "Date Created", hideOnMobile: true },
    { label: "Last Month Report", hideOnMobile: true },
    { label: "Last Six Months", hideOnMobile: true },
    { label: "Last Year", hideOnMobile: true },
    { label: "Email Month report", hideOnMobile: true },
    { label: "Delete", hideOnMobile: true },
  ];

  const renderTable = () => (
    <TableContainer
      sx={{
        pl: 2,
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                padding: "12px 8px",
                width: "50px",
              }}
            >
              Details
            </TableCell>
            {columns.map(({ label, hideOnMobile }) => (
              <TableCell
                key={label}
                sx={{
                  display: hideOnMobile
                    ? { xs: "none", md: "table-cell" }
                    : "table-cell",
                }}
              >
                <TableSortLabel
                  active={orderBy === label}
                  direction={orderBy === label ? order : "asc"}
                  onClick={() => handleRequestSort(label)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length > 0 ? (
            filteredData.map((grp, index) => (
              <React.Fragment key={grp.id}>
                <TableRow
                  key={grp.id}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(grp)}
                      sx={{ color: "white" }}
                    >
                      {expandedRows[grp] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body1"
                      onClick={() => goToUnitsSubs(grp)}
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": {
                          textDecoration: "underline",
                          color: "primary.dark",
                        },
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        padding: 0,
                        textAlign: "left",
                      }}
                    >
                      {grp.name}
                    </Link>
                  </TableCell>
                  <TableCell>{grp.customerGroupName}</TableCell>
                  <TableCell>{grp.description}</TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {new Date(grp.date_created).toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenLastReportDialog(grp)}
                      title="View Last Month Report"
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      color="success"
                      onClick={() => handleOpenLastSixReportDialog(grp)}
                      title="View Last Six Months Reports"
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      color="warning"
                      onClick={() => handleOpenLastYearReportDialog(grp)}
                      title="View Last Year Report"
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => handleSendEmail(grp)}
                      title="Send Monthly Report"
                    >
                      <EmailIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(grp)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={3}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[grp]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Date Created:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {new Date(grp.date_created).toLocaleString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                }
                              )}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Last Month Report:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <IconButton
                                color="primary"
                                onClick={() => handleOpenLastReportDialog(grp)}
                                title="View Last Month Report"
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Last six Month :
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <IconButton
                                color="success"
                                onClick={() =>
                                  handleOpenLastSixReportDialog(grp)
                                }
                                title="View Last Six Months Reports"
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Last year :
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <IconButton
                                color="warning"
                                onClick={() =>
                                  handleOpenLastYearReportDialog(grp)
                                }
                                title="View Last Year Report"
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Email Month report:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <IconButton
                                onClick={() => handleSendEmail(grp)}
                                title="Send Monthly Report"
                              >
                                <EmailIcon />
                              </IconButton>
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Delete:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(grp)}
                              >
                                Delete
                              </Button>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                No report groups found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {showDetails ? (
        <ReportGroupDetails
          group={selectedDetailsGroup}
          onBack={() => {
            setShowDetails(false);
            setSelectedDetailsGroup(null);
          }}
        />
      ) : loadingReportGroups ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : errorFetching ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {errorFetching}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold" m={2}>
              Report Groups
            </Typography>
          </Box>
          <Grid
            container
            spacing={{ xs: 2, md: 0 }}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            mb={2}
            px={2}
          >
            <Grid container gap={1} size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchReportGroups}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={addReportGroup}
              >
                Add Report Group
              </Button>
            </Grid>
            <Grid
              container
              justifyContent={"flex-end"}
              size={{ xs: 12, md: 6 }}
            >
              <TextField
                label="Search Report Group..."
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 200, width: 300 }}
              />
            </Grid>
          </Grid>
          {renderTable()}
        </>
      )}

      <AddReportGroup
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        onAdded={fetchReportGroups}
      />
      <LastMonthReportDialog
        open={lastMonthReportDialogOpen}
        onClose={() => setLastMonthReportDialogOpen(false)}
        groupId={selectedGroup?.id}
        groupName={selectedGroup?.name}
      />
      <LastSixMonthReportDialog
        open={lastSixMonthReportDialogOpen}
        onClose={() => setLastSixMonthReportDialogOpen(false)}
        groupId={selectedGroup?.id}
        groupName={selectedGroup?.name}
        title={"Last 6 Months Report"}
        mOffset={6}
      />
      <LastSixMonthReportDialog
        open={lastYearReportDialogOpen}
        onClose={() => setLastYearReportDialogOpen(false)}
        groupId={selectedGroup?.id}
        groupName={selectedGroup?.name}
        title={"Last Year Report"}
        mOffset={13}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      )}
    </Box>
  );
};

export default ReportGroups;
